import { useState, useRef } from "react";
import { Search, Check, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type OptionItem = string | { label: string; value: string };

interface SearchableSelectProps {
  options: OptionItem[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const getLabel = (item: OptionItem) => typeof item === "string" ? item : item.label;
  const getValue = (item: OptionItem) => typeof item === "string" ? item : item.value;

  const filtered = options.filter((o) =>
    getLabel(o).toLowerCase().includes(search.toLowerCase())
  );

  const selectedLabel = options.find(o => getValue(o) === value);
  const displayValue = selectedLabel ? getLabel(selectedLabel) : "";

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-lg border border-transparent bg-muted/60 px-3 py-2 text-sm transition-colors focus-within:bg-background",
            "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ring-offset-background",
            open && "ring-2 ring-ring ring-offset-2",
            !value && "text-muted-foreground"
          )}
        >
          <span className="truncate" title={displayValue || undefined}>{displayValue || placeholder}</span>
          <ChevronDown className={cn("w-4 h-4 text-muted-foreground shrink-0 transition-transform", open && "rotate-180")} />
        </button>
      </PopoverTrigger>

      {/* Portal-rendered so it can never be clipped by ancestor overflow */}
      <PopoverContent
        align="start"
        sideOffset={4}
        className="min-w-[var(--radix-popover-trigger-width)] w-max max-w-[min(28rem,90vw)] p-0 rounded-lg"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
          <Search className="w-4 h-4 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
          />
        </div>
        <div className="max-h-52 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">No results found</p>
          )}
          {filtered.map((item) => {
            const itemValue = getValue(item);
            const itemLabel = getLabel(item);
            return (
              <button
                key={itemValue}
                type="button"
                onClick={() => {
                  onChange(itemValue);
                  handleOpenChange(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-popover-foreground hover:bg-accent transition-colors text-left"
              >
                <div className={cn(
                  "w-4 h-4 rounded border flex items-center justify-center shrink-0",
                  value === itemValue ? "border-primary bg-primary" : "border-input"
                )}>
                  {value === itemValue && <Check className="w-3 h-3 text-primary-foreground" />}
                </div>
                {itemLabel}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
